import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import { typingSound } from '../utils/typing-sound'
import { postCalendlyBook } from '../api/voice-bot-client'
import {
  CALENDLY_SYSTEM_MESSAGES,
  DEFAULT_LANGUAGE,
  type LanguageCode,
} from '../config/language'
import type { ContactInfo, Message, ScheduledMeeting } from '../types'

// ============================================
// runBookMeeting — book_meeting tool handler (Wave 3b)
// ============================================
// Lifted from VoiceBotPanel.handleFunctionCall.
//
// Flow:
//   1. Mark scheduling-reached for analytics.
//   2. Deferred-mode gate: if no contact info yet, open the scheduling
//      popup, instruct the bot to tell the user, wait for the popup,
//      and bail out gracefully if the user closes it without filling.
//   3. Resolve the customer name (args > popup contact > transcript
//      fallback) and stash it on the contact-info ref.
//   4. POST the booking payload to calendly-book.
//   5. On success: commit ScheduledMeeting, open the Calendly embed,
//      set bookingConfirmed=false, send the function_call_output and
//      trigger response IN-LINE (so the model speaks the prompt while
//      the embed is open). The handler then signals `kind: 'handled'`
//      so the caller short-circuits the generic post-handler send.
//
// The discriminated return type preserves the original `return` that
// short-circuited handleFunctionCall — the caller maps `handled` to its
// own early return.

export type RunBookMeetingResult =
  | { kind: 'handled' }
  | { kind: 'result'; output: string }

export interface RunBookMeetingOptions {
  args: Record<string, unknown>
  transcript: Message[]
  customerId: string | undefined
  callId: string
  contactInfo: ContactInfo | null
  contactInfoRef: MutableRefObject<ContactInfo | null>
  reachedSchedulingRef: MutableRefObject<boolean>
  pendingCustomerNameRef: MutableRefObject<string | null>
  dataChannelRef: MutableRefObject<RTCDataChannel | null>
  scheduledMeetingRef: MutableRefObject<ScheduledMeeting | null>
  setScheduledMeeting: Dispatch<SetStateAction<ScheduledMeeting | null>>
  setShowCalendlyEmbed: (next: boolean) => void
  setBookingConfirmed: (next: boolean) => void
  waitForDeferredContactInfo: () => Promise<ContactInfo | null>
  extractCustomerNameFromTranscript: (
    transcript: { role: string; content: string }[]
  ) => string | undefined
  triggerResponse: (reason: string, immediate?: boolean) => void
  // Selected conversation language — used to keep the deferred-contact and
  // "calendar opened" system prompts in the user's language so the bot does
  // not drift to Hebrew at the Calendly stage.
  selectedLanguageRef: MutableRefObject<LanguageCode>
}

interface BookApiResponse {
  success?: boolean
  bookingUrl?: string
  eventDetails?: { formattedTime?: string }
}

export const runBookMeeting = async (
  opts: RunBookMeetingOptions
): Promise<RunBookMeetingResult> => {
  const {
    args,
    transcript,
    customerId,
    callId,
    contactInfo,
    contactInfoRef,
    reachedSchedulingRef,
    pendingCustomerNameRef,
    dataChannelRef,
    scheduledMeetingRef,
    setScheduledMeeting,
    setShowCalendlyEmbed,
    setBookingConfirmed,
    waitForDeferredContactInfo,
    extractCustomerNameFromTranscript,
    triggerResponse,
    selectedLanguageRef,
  } = opts

  const lang: LanguageCode = selectedLanguageRef.current || DEFAULT_LANGUAGE
  const langMessages =
    CALENDLY_SYSTEM_MESSAGES[lang] || CALENDLY_SYSTEM_MESSAGES[DEFAULT_LANGUAGE]

  console.log('📅 book_meeting called with args:', args)
  console.log('📅 contactInfo (state):', contactInfo)
  console.log('📅 contactInfo (ref):', contactInfoRef.current)

  // Track that scheduling was reached (for A/B analytics)
  reachedSchedulingRef.current = true

  // DEFERRED MODE GATE: If no contact info yet, show scheduling popup and wait
  let currentContactInfo = contactInfoRef.current
  if (!currentContactInfo) {
    console.log(
      '📅 Deferred mode: no contact info yet — showing scheduling popup before Calendly'
    )
    pendingCustomerNameRef.current = (args.customerName as string) || null
    typingSound.stop()

    if (dataChannelRef.current?.readyState === 'open') {
      // Localized deferred-contact prompt — keeps the bot in the
      // user-selected language (was previously hardcoded Hebrew, which
      // caused the bot to switch to Hebrew at the Calendly stage even
      // when the user picked English/Russian/French/Arabic).
      const nameSuffix = args.customerName
        ? ` Customer name on file: ${args.customerName}.`
        : ''
      dataChannelRef.current.send(
        JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [
              {
                type: 'input_text',
                text: `${langMessages.deferredContactPrompt}${nameSuffix}`,
              },
            ],
          },
        })
      )
      triggerResponse('deferred-contact-prompt')
    }

    currentContactInfo = await waitForDeferredContactInfo()
    console.log('📅 Scheduling popup completed:', currentContactInfo)
    if (!currentContactInfo?.email || !currentContactInfo?.phone) {
      typingSound.stop()
      return {
        kind: 'result',
        output:
          'BOOKING CANCELLED: The customer closed the details popup or did not complete it. Explain briefly that without email and phone you cannot open the calendar, and invite them to ask again when they are ready.',
      }
    }
  }

  const resolvedCustomerName =
    (args.customerName as string) ||
    currentContactInfo?.name ||
    extractCustomerNameFromTranscript(transcript)

  // Store customer name early (before Calendly confirms)
  if (resolvedCustomerName && currentContactInfo) {
    currentContactInfo.name = resolvedCustomerName
    contactInfoRef.current = currentContactInfo
    console.log('📅 Stored customer name:', resolvedCustomerName)
  }

  const bookingPayload = {
    slotStart: args.slotStart,
    customerName: resolvedCustomerName,
    customerEmail: currentContactInfo?.email || args.customerEmail || '',
    customerPhone: currentContactInfo?.phone || args.customerPhone || '',
    meetingType: args.meetingType,
    location: args.location,
    customerId, // For multi-tenant Calendly
  }
  console.log('📅 Sending booking payload:', bookingPayload)

  const bookResponse = await postCalendlyBook(bookingPayload)
  if (!bookResponse.ok) {
    console.error('📅 calendly-book HTTP error:', bookResponse.status)
    return {
      kind: 'result',
      output:
        'SYSTEM ERROR: Could not open booking form right now. Apologize and offer to take their contact details instead.',
    }
  }
  let bookData: BookApiResponse
  try {
    bookData = await bookResponse.json()
  } catch (parseErr) {
    console.error('📅 calendly-book JSON parse error:', parseErr)
    return {
      kind: 'result',
      output:
        'SYSTEM ERROR: Could not open booking form right now. Apologize and offer to take their contact details instead.',
    }
  }
  if (bookData.success) {
    console.log(
      '📅 Booking initiated, setting state for reminder tracking...'
    )
    const meetingData: ScheduledMeeting = {
      datetime: (args.slotStart as string) || '',
      formattedTime: bookData.eventDetails?.formattedTime || '',
      customerName: resolvedCustomerName || '',
      customerEmail: currentContactInfo?.email || '',
      customerPhone: currentContactInfo?.phone || '',
      bookingUrl: bookData.bookingUrl,
      source: 'calendly',
    }
    setScheduledMeeting(meetingData)
    scheduledMeetingRef.current = meetingData // Immediately update ref (don't wait for useEffect)
    setShowCalendlyEmbed(true)
    setBookingConfirmed(false)
    console.log(
      '📅 State set: showCalendlyEmbed=true, bookingConfirmed=false, meetingData:',
      meetingData
    )

    // Include the instruction IN the function output so the model knows to speak.
    // Sending separate user messages after function output causes empty responses.
    // Use the localized [SYSTEM: ...] prompt so the bot stays in the user's
    // chosen language (deferredContactPrompt above + bookingFormOpened here
    // were the two language-drift trigger points at the Calendly stage).
    const result = langMessages.bookingFormOpened

    // Send function output with embedded instruction (do NOT send as separate message!)
    // Stop typing sound — Calendly embed is opening
    typingSound.stop()
    if (dataChannelRef.current?.readyState === 'open') {
      dataChannelRef.current.send(
        JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: callId,
            output: JSON.stringify(result),
          },
        })
      )
      // Trigger response immediately (official OpenAI pattern)
      triggerResponse('book-meeting-calendly', true)
    }
    // Caller short-circuits the generic post-handler send.
    return { kind: 'handled' }
  }
  return {
    kind: 'result',
    output:
      'ERROR: Could not prepare the meeting booking link (tell customer in their language)',
  }
}
